// src/api/baseQuery.js
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_CONFIG } from './constants';
import AuthService from '../services/authService';
import { createErrorResponse } from '../types/api.types';
import TokenService from '../services/tokenService';

// Token refresh queue management (similar to interceptors.js)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const baseQuery = fetchBaseQuery({
  baseUrl: API_CONFIG.BASE_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    // 🔧 FIX: Don't add Authorization header for public authentication endpoints
    // These endpoints are for login/verification and should NOT have auth headers
    const publicEndpoints = ['loginGetCode', 'verifyAndGetToken', 'register'];
    const isPublicEndpoint = endpoint && publicEndpoints.includes(endpoint);
    
    if (!isPublicEndpoint) {
      const token = TokenService.getAccessToken();
      
      // Only add Authorization header if token exists and is valid (not 'undefined' or 'null' string)
      if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    // 🔧 CRITICAL FIX: RTK Query automatically sets Content-Type for POST/PUT/PATCH
    // We should NOT manually set it for GET requests - .NET backend throws error
    // "The input does not contain any JSON tokens" when Content-Type is application/json but body is empty
    
    return headers;
  }
});

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Skip token refresh for auth endpoints to avoid infinite loops
  const isAuthEndpoint = typeof args === 'string' 
    ? args.includes('/auth/') || args.includes('/Auth/')
    : args.url?.includes('/auth/') || args.url?.includes('/Auth/');
  
  if (isAuthEndpoint) {
    // For auth endpoints, just execute the request without token checks
    return await baseQuery(args, api, extraOptions);
  }

  // Check if token is valid before making request
  if (!TokenService.isAuthenticated()) {
    AuthService.logout();
    return {
      error: createErrorResponse(new Error('Oturum süresi doldu'), 'SESSION_EXPIRED')
    };
  }

  // Check if token needs refresh before making request
  const accessToken = TokenService.getAccessToken();
  if (accessToken && TokenService.isTokenExpired(accessToken)) {
    // If already refreshing, queue the request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          // Retry with new token
          return baseQuery(args, api, extraOptions);
        })
        .catch(err => {
          return {
            error: createErrorResponse(new Error('Token yenileme başarısız'), 'TOKEN_REFRESH_FAILED')
          };
        });
    }

    isRefreshing = true;

    try {
      const refreshResult = await AuthService.refreshToken();
      if (!refreshResult || !refreshResult.token) {
        processQueue(new Error('Token refresh failed'), null);
        AuthService.logout();
        return {
          error: createErrorResponse(new Error('Token yenileme başarısız'), 'TOKEN_REFRESH_FAILED')
        };
      }
      
      processQueue(null, refreshResult.token);
      isRefreshing = false;
      
      // Retry the original request with new token
      return await baseQuery(args, api, extraOptions);
    } catch (error) {
      processQueue(error, null);
      isRefreshing = false;
      AuthService.logout();
      return {
        error: createErrorResponse(new Error('Token yenileme hatası'), 'TOKEN_REFRESH_ERROR')
      };
    }
  }

  // Execute the request
  let result = await baseQuery(args, api, extraOptions);

  // Update last activity timestamp
  TokenService.updateLastActivity();

  // Handle errors
  if (result.error) {
    const { status, data } = result.error;

    switch (status) {
      case 401:
        // Token is invalid, attempt refresh with queue management
        if (isRefreshing) {
          // Queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => baseQuery(args, api, extraOptions))
            .catch(() => {
              AuthService.logout();
              return {
                error: createErrorResponse(new Error('Oturum geçersiz'), 'UNAUTHORIZED')
              };
            });
        }

        isRefreshing = true;

        try {
          const refreshResult = await AuthService.refreshToken();
          if (refreshResult && refreshResult.token) {
            processQueue(null, refreshResult.token);
            isRefreshing = false;
            
            // Retry the request with new token
            const retryResult = await baseQuery(args, api, extraOptions);
            if (!retryResult.error) {
              return retryResult;
            }
          }
          
          throw new Error('Token refresh failed');
        } catch (error) {
          // Refresh failed
          processQueue(error, null);
          isRefreshing = false;
          AuthService.logout();
          
          // 🛡️ IMPROVED: No hard redirect - let QueryStateHandler handle navigation
          console.log('🔐 BaseQuery: Token refresh failed, returning 401 error');
        }
        
        return {
          error: createErrorResponse(new Error('Oturum geçersiz'), 'UNAUTHORIZED')
        };

      case 403:
        console.log('🔐 BaseQuery: 403 Forbidden - returning error (QueryStateHandler will handle navigation)');
        
        // 🛡️ IMPROVED: No hard redirect - let QueryStateHandler handle navigation
        // This provides smoother UX and keeps React state consistent
        return {
          error: createErrorResponse(new Error('Bu işlem için yetkiniz yok'), 'FORBIDDEN')
        };

      case 404:
        return {
          error: createErrorResponse(new Error('Kaynak bulunamadı'), 'NOT_FOUND')
        };

      default:
        return {
          error: createErrorResponse(
            new Error(data?.message || 'Bir hata oluştu'),
            data?.type || 'TECHNICAL_ERROR'
          )
        };
    }
  }

  return result;
};